import { useEffect, useMemo, useRef, useState } from 'react';
import { MatchUiApp } from '../screens/match/match-screen.js';
import { SplashUiApp } from '../screens/splash/splash-screen.js';
import { ManagerUiApp } from '../screens/manager/manager-screen.js';
import type { AppRuntime, AppSnapshot } from './app-runtime.js';
import { createUiBridgeV2 } from './manager-ui-v2-boundary.js';
import { createMatchUiBridgeV2 } from './match-ui-v2-boundary.js';
import type { UiBridgeV2 } from './contracts/manager-ui-v2.js';
import type { MatchUiBridgeV2 } from './contracts/match-ui-v2.js';
import { initRouter, navigateTo, type RouteMatch } from './router.js';
import { routes } from './routes.js';

interface AppHostProps {
    runtime: AppRuntime;
}

function NotFoundScreen() {
    return (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background p-6 text-foreground">
            <div className="max-w-md rounded-lg border bg-card p-6 text-center">
                <h1 className="text-2xl font-semibold">Page Not Found</h1>
                <p className="mt-2 text-sm text-muted-foreground">This route does not exist in TeamSquad.</p>
            </div>
        </div>
    );
}

function RouteLoadingScreen() {
    return (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background text-foreground">
            <div className="text-sm text-muted-foreground">Loading...</div>
        </div>
    );
}

export function AppHost({ runtime }: AppHostProps) {
    const [snapshot, setSnapshot] = useState<AppSnapshot | null>(null);
    const [activeRoutePath, setActiveRoutePath] = useState<string>('/');
    const [pendingRoutePath, setPendingRoutePath] = useState<string | null>(null);
    const [hasResolvedInitialRoute, setHasResolvedInitialRoute] = useState<boolean>(false);
    const routePathRef = useRef<string>('/');

    const managerBridge = useMemo<UiBridgeV2>(() => (
        createUiBridgeV2(runtime, () => routePathRef.current, (path) => navigateTo(path))
    ), [runtime]);
    const matchBridge = useMemo<MatchUiBridgeV2>(() => createMatchUiBridgeV2(runtime), [runtime]);

    useEffect(() => runtime.subscribe(setSnapshot), [runtime]);

    useEffect(() => {
        const router = initRouter({
            routes,
            notFoundPath: '/404'
        });
        router.onNavigate(async (match: RouteMatch) => {
            setPendingRoutePath(match.path);
            if (match.path === '/manager') {
                navigateTo('/manager/home');
                return;
            }
            await runtime.onRoute(match.path, match.params);
            routePathRef.current = match.path;
            setActiveRoutePath(match.path);
            setPendingRoutePath(null);
            setHasResolvedInitialRoute(true);
        });
    }, [runtime]);

    if (!snapshot) {
        return null;
    }

    const routePath = pendingRoutePath ?? activeRoutePath ?? '/';
    if (routePath === '/') {
        return (
            <SplashUiApp
                busy={snapshot.splash.busy}
                canLoad={snapshot.splash.canLoadCampaign}
                onQuick={() => void runtime.splashQuick(snapshot.splash.teamName)}
                onLoad={() => void runtime.splashLoad()}
                onNew={() => void runtime.splashNew(snapshot.splash.teamName)}
            />
        );
    }

    if (routePath.startsWith('/manager')) {
        if (!snapshot.manager) {
            return hasResolvedInitialRoute ? <NotFoundScreen /> : <RouteLoadingScreen />;
        }
        return <ManagerUiApp bridge={managerBridge} />;
    }

    if (routePath === '/match') {
        return <MatchUiApp bridge={matchBridge} />;
    }

    if (routePath === '/404' || routePath === '/not-found') {
        return <NotFoundScreen />;
    }

    return <NotFoundScreen />;
}
